import { useOnBoardingSkipFlow } from '@common/onboarding/hooks';
import { Onboarding } from '@common/user-analytics/events';
import { CopilotLogoXs16pxIcon } from '@prophecy/icons';
import { BaseColor, negateVariable, theme } from '@prophecy/theme';
import { Centered, Stack, Text, Display, Button, Badges } from '@prophecy/ui-v3';
import { BadgesComponentNames } from '@prophecy/ui-v3/Badges/tokens';
import styled from 'styled-components';

import { ReactComponent as SparkLogo } from '../../assets/spark_logo.svg?react';
import { ReactComponent as SqlLogo } from '../../assets/sql_logo.svg?react';

const Card = styled.a`
  all: unset;
  cursor: pointer;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  flex-grow: 0;
  gap: ${theme.spaces.x4};
  padding: ${theme.spaces.x24};
  width: 440px;
  height: 198px;

  background: ${theme.colors.white};
  border-radius: ${theme.radius.x8};
  border: 1px solid ${theme.colors.white};
  box-shadow: ${theme.shadows.sm};
  &:hover,
  &:active {
    border: 1px solid ${theme.colors.brand300};
    box-shadow:
      ${theme.shadows.sm},
      0px 0px 0px 4px ${theme.colors.brand100};
  }
`;
const StyledCopilotLogo = styled(CopilotLogoXs16pxIcon)`
  width: 12px;
  height: 12px;
`;
const StyledBadges = styled(Badges)`
  &&& {
    margin-top: ${theme.spaces.x6};
    border-color: transparent;
    ${StyledCopilotLogo} {
      margin-left: ${negateVariable(theme.spaces.x4)};
    }
  }
  display: flex;
  align-items: center;
  justify-content: center;
  [data-component='${BadgesComponentNames.container}'] {
    gap: ${theme.spaces.x4};
    svg {
      position: relative;
      bottom: 1px;
    }
  }
`;
export function OnboardingHome() {
  const { track, navigate, onSkip, sqlOnboardingUrl, sparkOnboardingUrl } = useOnBoardingSkipFlow();

  function sqlClick() {
    track(
      Onboarding.onboarding_home.events.onboarding_home_flow_type,
      {
        onboarding_home_flow_type: 'sql'
      },
      Onboarding.onboarding_home.event_category
    );
    navigate(sqlOnboardingUrl);
  }
  function sparkClick() {
    track(
      Onboarding.onboarding_home.events.onboarding_home_flow_type,
      {
        onboarding_home_flow_type: 'spark'
      },
      Onboarding.onboarding_home.event_category
    );
    navigate(sparkOnboardingUrl);
  }
  return (
    <Stack height='100%'>
      <Centered>
        <Stack gap={theme.spaces.x48} alignY='center'>
          <Display level='xs' weight={theme.fontWeight.medium} color={theme.colors.gray900}>
            Welcome to Prophecy
          </Display>
          <Stack direction='horizontal' gap={theme.spaces.x24}>
            <Card onClick={sqlClick}>
              <Stack direction='horizontal'>
                <SqlLogo />
                <StyledBadges size='s' tone={BaseColor.purple} icon={<StyledCopilotLogo />} iconPlacement='left'>
                  <Text level='xs' weight={theme.fontWeight.semiBold} color={theme.colors.gray900}>
                    AI
                  </Text>
                </StyledBadges>
              </Stack>

              <Text level='xl' weight={theme.fontWeight.semiBold} color={theme.colors.gray900}>
                Low-code SQL
              </Text>
              <Text level='sm' weight={theme.fontWeight.normal} color={theme.colors.gray600}>
                Design a robust Pipeline powered by SQL and AI Copilot
              </Text>
            </Card>
            <Card onClick={sparkClick}>
              <SparkLogo />
              <Text level='xl' weight={theme.fontWeight.semiBold} color={theme.colors.gray900}>
                Low-code Spark
              </Text>
              <Text level='sm' weight={theme.fontWeight.normal} color={theme.colors.gray600}>
                Create a Pipeline using visual operators that turns into code
              </Text>
            </Card>
          </Stack>
          <Stack direction='horizontal' width='100%' align='end'>
            <Button variant='secondaryGray' onClick={onSkip} data-test-id='onboarding-skip-button'>
              Skip
            </Button>
          </Stack>
        </Stack>
      </Centered>
    </Stack>
  );
}
